import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { onBookingUpdated, onWindowRefocus } from '@/lib/bookingEvents';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Calendar, Scissors, Coffee, Settings2, ChevronRight, Clock,
  CheckCircle2, XCircle, MessageSquare, CalendarClock, ChevronLeft,
  CalendarPlus, Ban, UserX, HandCoins, DollarSign, MoonStar, RotateCcw, History, Sparkles, Check, MoreHorizontal, X, PenLine, Repeat } from 'lucide-react';
import { bookingBlockStyle } from '@/lib/serviceColors';
import { bookingApi } from '@/lib/bookingApi';
import { api } from '@/lib/api';
import { messagingApi } from '@/lib/messagingApi';
import { ManualBookingSheet } from '@/components/Profile/ManualBookingSheet';
import { SaleSheet } from '@/components/Bookings/SaleSheet';
import { PayLinkStatus } from '@/components/Bookings/PayLinkStatus';
import { RefundDialog } from '@/components/Bookings/RefundDialog';
import { PremiumRequestsBar, usePremiumRequests } from '@/components/Bookings/PremiumRequestsSheet';
import { PendingAssignCard } from '@/components/Bookings/PendingAssignCard';
import { PushPrompt } from '@/components/Notifications/PushPrompt';
import { useBlockDrag, canDragBooking, STRETCH_MIN } from '@/components/Profile/useBlockDrag';
import { useBookingMove } from '@/components/Profile/useBookingMove';
import { AgendaCascadeSheet } from '@/components/Profile/AgendaCascadeSheet';
import { EditBookingSheet } from '@/components/Profile/EditBookingSheet';
import { ScheduleConflictDialog } from '@/components/Profile/ScheduleConflictDialog';
import { BlockEditorSheet } from '@/components/Profile/BlockEditorSheet';
import { MonthCalendar } from '@/components/Bookings/MonthCalendar';
import { AgendaDateHeader } from '@/components/Bookings/AgendaDateHeader';
import { toLocalISO, formatTime12, formatDuration, formatDateShortEs } from '@/utils/bookingFormat';
import { toast } from 'sonner';
import { tr, localeTag } from '@/contexts/LanguageContext';
import { fmtMoney } from '@/utils/money';

export const PPH = 88; // px per hour

export const toMin = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};
export const toHHMM = (v) => `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
const fromISO = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); };

// Límites del grid PÚBLICO: la jornada, extendida a la hora redonda cuando hay
// tramos premium antes de abrir o después de cerrar (la banda dorada vive ahí).
const publicBounds = (schedule) => {
  if (!schedule?.work_start) return null;
  const wsCfg = toMin(schedule.work_start);
  const weCfg = toMin(schedule.work_end);
  let ws = wsCfg;
  let we = weCfg;
  (schedule.premium_tiers || []).forEach((t) => {
    ws = Math.min(ws, toMin(t.start));
    we = Math.max(we, toMin(t.end));
  });
  if (ws < wsCfg) ws = Math.floor(ws / 60) * 60;
  if (we > weCfg) we = Math.min(1440, Math.ceil(we / 60) * 60);
  return { ws, we, wsCfg, weCfg };
};

const STRIPES = {
  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 6px, rgba(255,255,255,0.035) 6px, rgba(255,255,255,0.035) 12px)',
};

export const PublicAgendaTimeline = ({ barber, isOwner = false }) => {
  const navigate = useNavigate();
  // Arreglo 4 (oct-2026) — deep link desde la campana: ?tab=agenda&date=AAAA-MM-DD abre ese día.
  const [selectedDay, setSelectedDay] = useState(() => {
    const d = isOwner ? new URLSearchParams(window.location.search).get('date') : null;
    return /^\d{4}-\d{2}-\d{2}$/.test(d || '') ? d : toLocalISO(new Date());
  });
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);        // owner: selected booking
  const [saleFor, setSaleFor] = useState(null);      // owner: Cobrar (venta)
  const [cancelTarget, setCancelTarget] = useState(null);
  const [refundFor, setRefundFor] = useState(null);    // D5: reembolso / anulación de cobro
  const [actionBusy, setActionBusy] = useState(false);
  // D5 · C3 — bandeja "Solicitudes pendientes" (premium); ?requests=1 la abre al llegar desde la campana.
  const [searchParams] = useSearchParams();
  const premiumInbox = usePremiumRequests(isOwner);

  // Deploy 2 · P2 — el owner navega 7 días hacia atrás (citas retroactivas
  // para cobrar) y 21 hacia adelante; el público sigue viendo 7 desde hoy.
  // "Hoy" es el del NEGOCIO (business_today del backend), no el reloj del visitante.
  const baseISO = schedule?.business_today || toLocalISO(new Date());
  // Deep link ?date= cuenta como elección manual (no se pisa con el «hoy» del negocio).
  const pickedRef = useRef(isOwner && /^\d{4}-\d{2}-\d{2}$/.test(new URLSearchParams(window.location.search).get('date') || ''));
  const [monthOpen, setMonthOpen] = useState(false);   // A1: calendario del mes desplegado
  const pickDay = (iso) => { pickedRef.current = true; setSelectedDay(iso); setMonthOpen(false); };
  const days = useMemo(() => {
    const back = isOwner ? 7 : 0;
    const ahead = isOwner ? 21 : 7;
    const inStrip = (() => { const b = fromISO(baseISO); const lo = new Date(b); lo.setDate(lo.getDate() - back); const hi = new Date(b); hi.setDate(hi.getDate() + ahead - 1); return selectedDay >= toLocalISO(lo) && selectedDay <= toLocalISO(hi); })();
    // A1: si el día elegido en el calendario cae fuera de la tira, la tira se centra en él.
    const base = isOwner && !inStrip ? fromISO(selectedDay) : fromISO(baseISO);
    return Array.from({ length: back + ahead }, (_, i) => {
      const d = new Date(base);
      d.setDate(d.getDate() + i - back);
      return d;
    });
  }, [isOwner, baseISO, selectedDay]);
  const todayBtnRef = useRef(null);
  useEffect(() => {
    if (isOwner) todayBtnRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [isOwner]);

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const res = await bookingApi.daySchedule(barber.username, selectedDay);
      setSchedule(res);
    } catch {
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, [barber.username, selectedDay]);
  const reloadSilent = useCallback(() => load({ silent: true }), [load]);

  useEffect(() => { load(); }, [load]);

  // Si el visitante está en otro huso, arrancamos sobre el "hoy" de la barbería
  // (solo mientras no haya elegido un día a mano).
  useEffect(() => {
    const bt = schedule?.business_today;
    if (bt && !pickedRef.current && bt !== selectedDay) setSelectedDay(bt);
  }, [schedule]); // eslint-disable-line react-hooks/exhaustive-deps

  // P1-1 — sincronización global de citas sin refresh (silenciosa: no desmonta el grid).
  useEffect(() => {
    const off1 = onBookingUpdated(reloadSilent);
    const off2 = onWindowRefocus(reloadSilent);
    // 21-oct-2026 — pago desde la app del cliente ⇒ la campana trae «pago recibido» ⇒ la agenda lo muestra pagado al instante.
    window.addEventListener('lineage_notifications_arrived', reloadSilent);
    return () => { off1(); off2(); window.removeEventListener('lineage_notifications_arrived', reloadSilent); };
  }, [reloadSilent]);

  // Deploy 3 · P1 — mover citas arrastrando (reschedule → 409 → cascada) + Deshacer.
  // Optimista (rendimiento D): la cita cambia de estado al instante; si el backend falla, vuelve como estaba.
  const patchLocal = useCallback((bookingId, patch) => setSchedule((prev) => prev ? ({
    ...prev, busy: (prev.busy || []).map((b) => (b.booking_id === bookingId ? { ...b, ...patch } : b)),
  }) : prev), []);
  const mover = useBookingMove({ selectedDay, schedule, reload: reloadSilent, patchLocal });

  const goBook = (time = '') => {
    const qs = time ? `?date=${selectedDay}&time=${encodeURIComponent(time)}` : '';
    navigate(`/barber/${encodeURIComponent(barber.username)}/reservar${qs}`);
  };

  const onlineOff = schedule && schedule.online_booking_enabled === false && !isOwner;
  const isClosed = Boolean(schedule?.closed);
  const dayHours = loading ? undefined : (isClosed || schedule?.closed_day) ? 'closed' : schedule?.work_start ? { start: schedule.work_start, end: schedule.work_end } : null;
  const interval = Number(schedule?.slot_interval_minutes) || 30;
  const pubBounds = useMemo(() => publicBounds(schedule), [schedule]);
  const premiumTiers = schedule?.premium_tiers || [];

  // La disponibilidad la decide SOLO el backend (free_slots / premium_slots ya vienen
  // calculados con el huso de la barbería y su anticipación mínima). Jamás el reloj del visitante.
  const resolveTap = (mins) => {
    const inRange = (r) => r && mins >= toMin(r.start) && mins < toMin(r.end);
    if ([...(schedule.busy || []), ...(schedule.blocked || []), ...(schedule.breaks || []), schedule.break].some(inRange)) {
      return { reason: tapReason(mins) };
    }
    // Hotfix 20-oct-2026: los huecos ya no siguen la rejilla del intervalo (p. ej. 15:20, 15:35…):
    // se toma el horario libre más cercano al punto tocado (preferido el anterior, dentro del intervalo).
    const nearest = (list, key) => {
      let best = null;
      for (const s of list) {
        const m = toMin(key(s));
        const dist = mins >= m ? mins - m : (m - mins) * 2; // el anterior cuenta la mitad que el siguiente
        if (dist < interval && (!best || dist < best.dist)) best = { s, dist };
      }
      return best?.s || null;
    };
    const free = nearest(schedule.free_slots || [], (t) => t);
    if (free) return { time: free, premium: false };
    const prem = nearest(schedule.premium_slots || [], (p) => p.time);
    if (prem) return { time: prem.time, premium: true };
    return { reason: tapReason(mins) };
  };

  const tapReason = (mins) => {
    const inRange = (r) => r && mins >= toMin(r.start) && mins < toMin(r.end);
    if ((schedule.busy || []).some(inRange)) return tr("Este horario ya está ocupado");
    if ((schedule.blocked || []).some(inRange)) return tr("El barbero bloqueó este horario");
    if (inRange(schedule.break)) return tr("Es el descanso del barbero");
    const today = schedule.business_today;
    if (today && selectedDay < today) return tr("Ese día ya pasó");
    if (today && selectedDay === today && schedule.business_now) {
      const nowMin = toMin(schedule.business_now);
      const minAdv = Number(schedule.min_advance_minutes) || 0;
      if (mins < nowMin) return tr("Ese horario ya pasó");
      if (mins < nowMin + minAdv) return tr("Este barbero pide reservar con al menos {0} de anticipación", formatDuration(minAdv));
    }
    const outside = mins < toMin(schedule.work_start) || mins >= toMin(schedule.work_end);
    if (outside) return premiumTiers.some(inRange) ? tr("Ese horario premium no está disponible") : tr("Fuera del horario de atención");
    return tr("Ese horario no está disponible");
  };

  const handleGridTap = (e) => {
    if (isOwner || !schedule?.work_start || !pubBounds) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mins = pubBounds.ws + ((e.clientY - rect.top) / PPH) * 60;
    const hit = resolveTap(mins);
    if (hit.time) {
      goBook(hit.time);
    } else {
      toast.info(hit.reason);
    }
  };

  const setStatus = async (booking, status) => {
    setActionBusy(true);
    const before = schedule;
    patchLocal(booking.booking_id, { status });
    setDetail(null);
    try {
      await bookingApi.updateBookingStatus(booking.booking_id, status);
      const msgs = {
        completed: tr("Cita marcada como completada"),
        no_show: tr("Cliente marcado como no asistió"),
        cancelled: tr("Cita cancelada — el horario quedó libre"),
      };
      toast.success(msgs[status] || tr("Cita actualizada"));
      load({ silent: true });
    } catch (err) {
      setSchedule(before);
      toast.error(err?.message || tr("No pudimos actualizar la cita"));
    } finally {
      setActionBusy(false);
    }
  };

  const openChat = async (booking) => {
    try {
      const res = await messagingApi.openConversation(booking.client_username);
      const cid = res?.conversation?.id;
      if (cid) navigate(`/messages/${cid}`);
    } catch {
      toast.error(tr("No pudimos abrir la conversación"));
    }
  };

  // ── Iter 193: selección long-press y acciones del dueño ──
  const [selection, setSelection] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [slotMenuOpen, setSlotMenuOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [absenceOpen, setAbsenceOpen] = useState(false);

  const handleSelectRange = useCallback((start, end) => {
    setSelection({ start, end });
  }, []);

  const closeSlotMenu = () => { setSlotMenuOpen(false); setSelection(null); };


  // LOTE 4 (founder 14-oct-2026): «Bloquear» abre el editor (nombre, repetir, conflictos); tocar un bloqueo/descanso lo edita.
  const [blockEditor, setBlockEditor] = useState(null); // { initial } | { block }
  const applyBlock = () => {
    if (!selection) return;
    setBlockEditor({ initial: { start: toHHMM(selection.start), end: toHHMM(selection.end) } });
  };

  // Founder 14-oct-2026: menú inline anclado al bloque (no modal) para que los tiradores de duración sigan usables.
  const selectionMenu = selection ? (
    <div className="flex items-center gap-1.5 rounded-full bg-card/95 backdrop-blur border border-border shadow-xl p-1" data-testid="slot-inline-menu">
      <Button size="sm" onClick={() => setManualOpen(true)} className="h-8 rounded-full px-3 text-xs gap-1.5" data-testid="slot-action-new-booking">
        <CalendarPlus className="w-3.5 h-3.5" />{tr("Cita nueva")}</Button>
      <Button size="sm" variant="outline" onClick={applyBlock} disabled={actionBusy} className="h-8 rounded-full px-3 text-xs gap-1.5 border-border" data-testid="slot-action-block">
        <Ban className="w-3.5 h-3.5" />{tr("Bloquear")}</Button>
      <Button size="sm" variant="ghost" onClick={() => setSlotMenuOpen(true)} className="h-8 w-8 rounded-full p-0" aria-label={tr("Más opciones")} data-testid="slot-action-more">
        <MoreHorizontal className="w-4 h-4" /></Button>
      <Button size="sm" variant="ghost" onClick={closeSlotMenu} className="h-8 w-8 rounded-full p-0 text-muted-foreground" aria-label={tr("Cancelar")} data-testid="slot-action-cancel">
        <X className="w-4 h-4" /></Button>
    </div>
  ) : null;

  const askAbsence = () => {
    if ((schedule?.busy || []).length > 0) {
      toast.error(tr("Tienes citas ese día — cancélalas o reprográmalas antes de marcar ausencia"));
      return;
    }
    setAbsenceOpen(true);
  };

  const applyAbsence = async () => {
    setActionBusy(true);
    try {
      const s = await bookingApi.getMySettings();
      const dates = Array.from(new Set([...(s.blocked_dates || []), selectedDay]));
      await bookingApi.saveMySettings({ blocked_dates: dates });
      toast.success(tr("Día marcado como ausencia — tu agenda aparece cerrada"));
      setAbsenceOpen(false);
      closeSlotMenu();
      load();
    } catch (err) {
      toast.error(err?.message || tr("No pudimos marcar la ausencia"));
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-4 sm:p-5" data-testid="barber-agenda-tab">
      {isOwner && (
        <button
          onClick={() => navigate('/settings/citas')}
          className="w-full mb-4 bg-primary/10 border border-primary/30 rounded-xl px-3.5 py-2.5 flex items-center gap-2 hover:bg-primary/15 transition-colors text-left group"
          data-testid="agenda-owner-config-link"
        >
          <Settings2 className="w-3.5 h-3.5 text-primary shrink-0" />
          <span className="flex-1 text-xs text-foreground/80">{tr("Gestiona tu agenda en")}{' '}<span className="font-semibold text-primary">{tr("Citas y Agenda")}</span></span>
          <ChevronRight className="w-3.5 h-3.5 text-primary group-hover:translate-x-0.5 transition-transform" />
        </button>
      )}

      {isOwner && <PushPrompt reason={tr("Nueva reserva, cancelación del cliente y pago recibido, al instante en tu teléfono.")} />}
      {isOwner && <PendingAssignCard onChanged={reloadSilent} />}

      {isOwner && (
        <PremiumRequestsBar
          data={premiumInbox.data}
          reload={premiumInbox.reload}
          onChanged={reloadSilent}
          autoOpen={searchParams.get('requests') === '1'}
        />
      )}

      {/* Header: fecha + horario del día (A1: dueño → encabezado desplegable con calendario del mes) */}
      {isOwner && (
        <div className="mb-3 space-y-3">
          <AgendaDateHeader selected={selectedDay} today={baseISO} hours={onlineOff ? null : dayHours} open={monthOpen}
                            onToggle={() => setMonthOpen((v) => !v)} onToday={() => pickDay(baseISO)} />
          {monthOpen && <MonthCalendar barberRef={barber.username} selected={selectedDay} today={baseISO} onPick={pickDay} />}
        </div>
      )}
      <div className={`flex items-end justify-between gap-3 mb-3 ${isOwner ? 'hidden' : ''}`}>
        <div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            <h3 className="font-display text-base font-bold text-foreground">{tr("Agenda")}</h3>
          </div>
          <p className="font-display text-lg font-bold text-primary capitalize leading-tight mt-0.5" data-testid="agenda-selected-date">
            {formatDateShortEs(selectedDay)}
          </p>
        </div>
        <div className="text-right" data-testid="agenda-day-hours">
          {loading || onlineOff ? null : isClosed ? (
            <Badge variant="outline" className="border-border text-muted-foreground uppercase tracking-wider text-[10px]">{tr("Cerrado")}</Badge>
          ) : schedule?.closed_day ? (
            <Badge variant="outline" className="border-border text-muted-foreground uppercase tracking-wider text-[10px]" data-testid="agenda-closed-day-badge">
              {schedule.closed_day === 'blocked' ? tr("Ausencia") : tr("Día cerrado")} · {tr("solo citas manuales")}
            </Badge>
          ) : schedule?.work_start ? (
            <p className="text-xs text-muted-foreground">{tr("Horario del día")}<span className="block text-sm font-semibold text-foreground tabular-nums">
                {formatTime12(schedule.work_start)} – {formatTime12(schedule.work_end)}
              </span>
            </p>
          ) : null}
        </div>
      </div>

      {onlineOff ? (
        <div className="text-center py-8" data-testid="agenda-online-disabled">
          <p className="text-sm text-muted-foreground">{tr("Este barbero no acepta reservas online por ahora.")}</p>
        </div>
      ) : (
        <>
          {/* Selector de días */}
          <div className="-mx-1 overflow-x-auto no-scrollbar mb-3">
            <div className="flex items-center gap-2 px-1 w-max min-w-full">
              {days.map((d) => {
                const iso = toLocalISO(d);
                const active = iso === selectedDay;
                const isTodayBtn = iso === baseISO;
                const isPastDay = iso < baseISO;
                return (
                  <button
                    key={iso}
                    ref={isTodayBtn ? todayBtnRef : undefined}
                    type="button"
                    onClick={() => { pickedRef.current = true; setSelectedDay(iso); }}
                    className={`flex flex-col items-center justify-center min-w-[52px] px-2 py-2 rounded-xl border transition-colors shrink-0 ${
                      active
                        ? 'bg-primary border-primary text-primary-foreground shadow-[0_2px_10px_rgba(201,168,76,0.35)]'
                        : isTodayBtn
                          ? 'bg-background border-primary text-primary'
                          : isPastDay
                            ? 'bg-background border-border/60 text-muted-foreground/60 hover:border-primary/40'
                            : 'bg-background border-border text-muted-foreground hover:border-primary/40'
                    }`}
                    data-testid={`agenda-day-${iso}`}
                    data-past={isPastDay ? 'true' : undefined}
                    data-today={isTodayBtn ? 'true' : undefined}
                    data-selected={active ? 'true' : undefined}
                  >
                    <span className="text-[10px] uppercase tracking-wider font-semibold">
                      {d.toLocaleDateString(localeTag(), { weekday: 'short' }).replace('.', '')}
                    </span>
                    <span className="font-display text-lg font-bold leading-tight">{d.getDate()}</span>
                    {isTodayBtn && <span className="text-[8px] uppercase tracking-wider font-semibold leading-none">{tr("Hoy")}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center" data-testid="agenda-loading">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : isClosed ? (
            <div className="text-center py-10" data-testid="agenda-closed">
              <p className="font-display text-lg font-bold text-muted-foreground mb-1">{tr("Cerrado")}</p>
              <p className="text-sm text-muted-foreground">{tr("El barbero no atiende este día.")}</p>
              <p className="text-xs text-muted-foreground/70 mt-1">{tr("Elige otra fecha para ver disponibilidad.")}</p>
            </div>
          ) : !schedule?.work_start ? (
            <div className="text-center py-10">
              <p className="text-sm text-muted-foreground">{tr("No pudimos cargar la agenda. Intenta de nuevo.")}</p>
            </div>
          ) : (
            <>
              <p className="text-[11px] text-muted-foreground mb-3 px-0.5" data-testid="agenda-hint">
                {isOwner
                  ? tr("Toca una cita para gestionarla · mantén presionada una cita para moverla (arrástrala hasta el borde para salir del horario) · mantén presionado un espacio vacío para crear una cita o bloquear, también en horas pasadas o fuera de tu horario.")
                  : tr("Los espacios vacíos están libres — toca uno para reservar.")}
              </p>
              {!isOwner && premiumTiers.length > 0 && (
                <p className="text-[11px] text-primary/90 mb-3 px-0.5 inline-flex items-start gap-1.5" data-testid="agenda-premium-legend">
                  <Sparkles className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <span>{tr("La franja dorada es")}{' '}<span className="font-semibold">{tr("Horario Premium")}</span>{' '}{tr("({0}): fuera de la jornada y con recargo. Al tocarlo envías una solicitud; el barbero la aprueba o rechaza y te avisamos.", premiumTiers.map((t) => `${formatTime12(t.start)}–${formatTime12(t.end)} ${t.label}`).join(' · '))}</span>
                </p>
              )}

              <DayGrid
                schedule={schedule}
                selectedDay={selectedDay}
                isOwner={isOwner}
                publicBounds={pubBounds}
                onGridTap={handleGridTap}
                onBookingTap={(b) => (isOwner ? setDetail(b) : toast.info(tr("Este horario ya está ocupado")))}
                selection={selection}
                onSelectRange={handleSelectRange}
                onResizeSelection={(start, end) => setSelection({ start, end })}
                selectionMenu={selectionMenu}
                onMoveBooking={mover.moveBooking}
                onBlockTap={(b) => setBlockEditor({ block: b })}
              />
            </>
          )}

          {isOwner && mover.saving && (
            <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 bg-card border border-border rounded-full px-4 py-2 flex items-center gap-2 shadow-xl" data-testid="agenda-saving">
              <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-foreground">{tr("Guardando…")}</span>
            </div>
          )}
          {isOwner && (<>
            <AgendaCascadeSheet cascade={mover.cascade} saving={mover.saving} onCancel={() => mover.setCascade(null)} onConfirm={mover.confirmCascade} />
            <ScheduleConflictDialog open={Boolean(mover.conflict)} busy={mover.saving} rules={mover.conflict?.rules || []} verb="mover" onContinue={mover.confirmOverlap} onBack={() => mover.setConflict(null)} />
          </>)}

          {!isOwner && (
            <Button
              onClick={() => goBook()}
              className="w-full mt-4 h-10 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
              data-testid="agenda-book-cta"
            >
              <Calendar className="w-4 h-4" />{tr("Agendar cita")}</Button>
          )}
        </>
      )}

      {/* Owner: detalle de cita */}
      <BookingDetailSheet
        booking={detail}
        barber={barber}
        date={selectedDay}
        open={Boolean(detail)}
        onOpenChange={(o) => !o && setDetail(null)}
        actionBusy={actionBusy}
        onReload={reloadSilent}
        onComplete={(b) => setStatus(b, 'completed')}
        onNoShow={(b) => setStatus(b, 'no_show')}
        onCancel={(b) => setCancelTarget(b)}
        onRefund={(b) => { setRefundFor(b); setDetail(null); }}
        onEdit={(b) => { setEditFor(b); setDetail(null); }}
        onMessage={openChat}
        onRescheduled={() => { setDetail(null); load(); }}
        onCharge={(b) => { setSaleFor(b); setDetail(null); }}
        onResendReceipt={async (b) => {
          setActionBusy(true);
          try {
            const res = await bookingApi.resendReceipt(b.booking_id || b.id);
            toast.success(res?.receipt_status === 'sent'
              ? tr("Recibo reenviado al correo del cliente")
              : tr("Recibo procesado (revisa el correo del cliente)"));
          } catch (err) {
            toast.error(err?.message || tr("No se pudo reenviar el recibo"));
          } finally {
            setActionBusy(false);
          }
        }}
      />

      {/* Owner: Cobrar (venta) */}
      <SaleSheet
        booking={saleFor}
        open={Boolean(saleFor)}
        onOpenChange={(o) => !o && setSaleFor(null)}
        onPaid={() => load()}
      />

      {/* D5 — Reembolsar (Stripe) / Anular cobro (efectivo): la cita queda cancelada */}
      <RefundDialog
        bookingId={refundFor?.booking_id || refundFor?.id}
        open={Boolean(refundFor)}
        onOpenChange={(o) => !o && setRefundFor(null)}
        onDone={() => load()}
      />

      <EditBookingSheet
        booking={editFor}
        open={Boolean(editFor)}
        onOpenChange={(o) => !o && setEditFor(null)}
        onSaved={() => { setEditFor(null); load(); }}
        onCancelBooking={(b) => { setEditFor(null); setCancelTarget(b); }}
      />

      <AlertDialog open={Boolean(cancelTarget)} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>{tr("¿Cancelar esta cita?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget && (
                <>{tr("Se cancelará “{0}” con {1} a las {2}. El horario quedará libre y el cliente recibirá una notificación.", cancelTarget.service_name, cancelTarget.client_name, formatTime12(cancelTarget.start))}</>
              )}
              {cancelTarget?.series_id && <span className="block mt-2 text-primary" data-testid="agenda-cancel-series-hint">{tr("Esta cita es parte de una serie recurrente.")}</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">{tr("Volver")}</AlertDialogCancel>
            {cancelTarget?.series_id && (
              <AlertDialogAction
                onClick={async () => { const t = cancelTarget; setCancelTarget(null); setDetail(null); try { const r = await api.post(`/bookings/series/${t.series_id}/cancel`, { from_booking_id: t.booking_id, scope: 'following' }); toast.success(tr("Canceladas {0} citas de la serie", r.cancelled.length)); reloadSilent(); } catch (e) { toast.error(e.message); } }}
                className="rounded-full border border-destructive/60 bg-transparent text-destructive hover:bg-destructive/10"
                data-testid="agenda-cancel-series-following"
              >{tr("Esta y las siguientes")}</AlertDialogAction>
            )}
            <AlertDialogAction
              onClick={() => { const t = cancelTarget; setCancelTarget(null); if (t) setStatus(t, 'cancelled'); }}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="agenda-cancel-confirm"
            >{cancelTarget?.series_id ? tr("Solo esta") : tr("Cancelar cita")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Owner: menú de acciones del bloque seleccionado */}
      <Sheet open={slotMenuOpen} onOpenChange={(o) => { if (!o) setSlotMenuOpen(false); }}>
        <SheetContent side="bottom" className="bg-card border-border rounded-t-2xl" data-testid="slot-action-menu">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-lg text-foreground flex items-center gap-2">
              <CalendarPlus className="w-4 h-4 text-primary" />
              {selection ? `${formatTime12(toHHMM(selection.start))} – ${formatTime12(toHHMM(selection.end))}` : tr("Nuevo bloque")}
            </SheetTitle>
            <p className="text-xs text-muted-foreground capitalize" data-testid="slot-menu-date">{formatDateShortEs(selectedDay)}</p>
          </SheetHeader>
          <div className="mt-3 space-y-2">
            <Button
              onClick={() => { setSlotMenuOpen(false); setManualOpen(true); }}
              className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground justify-start gap-2.5"
              data-testid="slot-sheet-new-booking"
            >
              <CalendarPlus className="w-4 h-4" />{' '}{tr("Cita nueva")}</Button>
            <Button
              onClick={applyBlock}
              disabled={actionBusy}
              variant="outline"
              className="w-full h-11 rounded-xl border-border justify-start gap-2.5 text-foreground"
              data-testid="slot-sheet-block"
            >
              <Ban className="w-4 h-4 text-muted-foreground" />{' '}{tr("Bloquear horario")}</Button>
            <Button
              onClick={askAbsence}
              disabled={actionBusy}
              variant="outline"
              className="w-full h-11 rounded-xl border-border justify-start gap-2.5 text-foreground"
              data-testid="slot-action-absence"
            >
              <UserX className="w-4 h-4 text-muted-foreground" />{' '}{tr("Marcar ausencia (todo el día)")}</Button>
            <Button
              onClick={() => setSlotMenuOpen(false)}
              variant="ghost"
              className="w-full h-10 rounded-xl text-muted-foreground"
              data-testid="slot-sheet-cancel"
            >{tr("Cancelar")}</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Owner: confirmación de ausencia */}
      <AlertDialog open={absenceOpen} onOpenChange={setAbsenceOpen}>
        <AlertDialogContent className="bg-card border-border" data-testid="absence-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="capitalize">{tr("¿Marcar ausencia el {0}?", formatDateShortEs(selectedDay))}</AlertDialogTitle>
            <AlertDialogDescription>{tr("Tu agenda aparecerá cerrada todo el día y no se aceptarán reservas online. Puedes revertirlo en Citas y Agenda.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">{tr("Volver")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={applyAbsence}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="absence-confirm-btn"
            >{tr("Marcar ausencia")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Owner: formulario de cita manual */}
      <BlockEditorSheet
        open={Boolean(blockEditor)}
        onOpenChange={(o) => { if (!o) { setBlockEditor(null); setSelection(null); } }}
        date={selectedDay}
        initial={blockEditor?.initial || null}
        block={blockEditor?.block || null}
        onSaved={() => { closeSlotMenu(); load(); }}
      />
      <ManualBookingSheet
        open={manualOpen}
        onOpenChange={(o) => { setManualOpen(o); if (!o) setSelection(null); }}
        barber={barber}
        date={selectedDay}
        startMin={selection?.start}
        endMin={selection?.end}
        workStart={schedule?.work_start}
        workEnd={schedule?.work_end}
        onCreated={() => { setManualOpen(false); setSelection(null); load(); }}
      />
    </div>
  );
};

// ── Clean day grid: hour lines + event blocks + owner long-press selection ──
// 1A (oct-2026) — la agenda trabaja en pasos de 5 min (servicios de 5 a 240 min, cualquier minuto).
const SNAP_MIN = 5;
const snapFloor = (m) => Math.floor(m / SNAP_MIN) * SNAP_MIN;
const snapCeil = (m) => Math.ceil(m / SNAP_MIN) * SNAP_MIN;

// F2 (oct-2026) — `bounds` fija el eje (columnas alineadas), `hideAxis` quita las horas (eje compartido), `hideChrome` quita las franjas superior/inferior.
export const DayGrid = ({ schedule, selectedDay, isOwner, publicBounds: pub, onGridTap, onBookingTap, selection, onSelectRange, onResizeSelection = null, selectionMenu = null, onMoveBooking, bounds = null, hideAxis = false, hideChrome = false, showPrice = false, onBlockTap = null }) => {
  const wsCfg = toMin(schedule.work_start);
  const weCfg = toMin(schedule.work_end);
  const AX = hideAxis ? 0 : 68;

  const blocks = useMemo(() => {
    const out = [];
    (schedule.busy || []).forEach((b) => out.push({ ...b, type: 'busy', startMin: toMin(b.start), endMin: toMin(b.end) }));
    const breaks = schedule.breaks?.length ? schedule.breaks : (schedule.break ? [schedule.break] : []);
    breaks.forEach((r) => out.push({ ...r, type: 'break', startMin: toMin(r.start), endMin: toMin(r.end) }));
    (schedule.blocked || []).forEach((r) => out.push({ ...r, type: 'blocked', startMin: toMin(r.start), endMin: toMin(r.end) }));
    return out;
  }, [schedule]);

  // B3 (founder 14-oct-2026) — citas encimadas lado a lado: carriles por clúster de solape (ancho 1/n).
  const lanes = useMemo(() => {
    const items = blocks.filter((b) => b.type === 'busy' && b.status !== 'cancelled')
      .map((b, i) => ({ i, s: b.startMin, e: b.endMin })).sort((a, b) => a.s - b.s || a.e - b.e);
    const out = new Map();
    let cluster = [], clusterEnd = -1, laneEnds = [];
    const flush = () => { cluster.forEach((c) => out.set(c.i, { lane: c.lane, n: laneEnds.length })); cluster = []; laneEnds = []; };
    items.forEach((it) => {
      if (cluster.length && it.s >= clusterEnd) flush();
      let lane = laneEnds.findIndex((end) => end <= it.s);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.e); } else laneEnds[lane] = it.e;
      cluster.push({ ...it, lane });
      clusterEnd = Math.max(clusterEnd, it.e);
    });
    flush();
    return out;
  }, [blocks]);

  // Público — bandas "Horario Premium": los tramos configurados recortados a la parte
  // FUERA de la jornada (antes de abrir / después de cerrar). Solo presentación: el tap
  // lo resuelve el padre contra premium_slots del backend.
  const premiumBands = useMemo(() => {
    if (isOwner) return [];
    const iv = Number(schedule.slot_interval_minutes) || 30;
    const out = [];
    (schedule.premium_tiers || []).forEach((t) => {
      const s = toMin(t.start);
      const e = toMin(t.end);
      if (s < wsCfg) out.push({ ...t, startMin: s, endMin: Math.min(e, wsCfg) });
      if (e > weCfg) out.push({ ...t, startMin: Math.max(s, weCfg), endMin: e });
    });
    const avail = (schedule.premium_slots || []).map((p) => toMin(p.time));
    // La etiqueta se coloca en el primer hueco sin cita/bloqueo para que nunca quede tapada.
    const covered = (m) => blocks.some((b) => m < b.endMin && m + iv > b.startMin);
    return out.filter((b) => b.endMin > b.startMin).map((b) => {
      let labelMin = b.startMin;
      while (labelMin + iv <= b.endMin && covered(labelMin)) labelMin += iv;
      if (labelMin + iv > b.endMin) labelMin = b.startMin;
      return { ...b, labelMin, available: avail.filter((m) => m >= b.startMin && m < b.endMin).length };
    });
  }, [schedule, isOwner, wsCfg, weCfg, blocks]);

  // Público — zonas fuera de la jornada SIN premium (rayadas): [ws, wsCfg) y [weCfg, we) menos las bandas.
  const publicStripes = useMemo(() => {
    if (isOwner || !pub) return [];
    const subtract = (s, e) => {
      let segs = [[s, e]];
      premiumBands.forEach((b) => {
        segs = segs.flatMap(([a, z]) => {
          if (b.endMin <= a || b.startMin >= z) return [[a, z]];
          const r = [];
          if (b.startMin > a) r.push([a, b.startMin]);
          if (b.endMin < z) r.push([b.endMin, z]);
          return r;
        });
      });
      return segs.filter(([a, z]) => z > a);
    };
    return [...subtract(pub.ws, wsCfg), ...subtract(weCfg, pub.we)];
  }, [isOwner, pub, wsCfg, weCfg, premiumBands]);

  // Deploy 3 · P3 — límites dinámicos (owner): min(work_start, bloque más temprano) …
  // max(work_end, bloque más tardío), redondeados a la hora, + zona de estiramiento
  // (60 min por borde) mientras se arrastra una cita. El público ve la jornada
  // (+ la franja premium si existe).
  const [stretch, setStretch] = useState({ before: 0, after: 0 });
  useEffect(() => { setStretch({ before: 0, after: 0 }); }, [schedule]);
  let ws = wsCfg;
  let we = weCfg;
  if (bounds) {
    ws = bounds.ws;
    we = bounds.we;
  } else if (isOwner) {
    const minB = Math.min(wsCfg, ...blocks.map((b) => b.startMin));
    const maxB = Math.max(weCfg, ...blocks.map((b) => b.endMin));
    ws = Math.max(0, Math.floor(minB / 60) * 60 - stretch.before);
    we = Math.min(1440, Math.ceil(maxB / 60) * 60 + stretch.after);
  } else if (pub) {
    ws = pub.ws;
    we = pub.we;
  }
  const totalH = ((we - ws) / 60) * PPH;
  const yOf = (m) => ((m - ws) / 60) * PPH;
  const boundsRef = useRef({ ws, we });
  boundsRef.current = { ws, we };

  // Long-press + drag selection (owner only) — move/up tracked on window
  const layerRef = useRef(null);
  const pressRef = useRef(null);
  const dragRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const setDragState = (v) => { dragRef.current = v; setDrag(v); };

  // Deploy 3 · P1 — arrastre de bloques (citas pending/confirmed) para moverlas.
  const onStretch = useCallback((dir) => {
    if (bounds) return;
    setStretch((s) => (dir === 'before' ? { ...s, before: s.before + STRETCH_MIN } : { ...s, after: s.after + STRETCH_MIN }));
  }, [bounds]);
  const blockDrag = useBlockDrag({ enabled: isOwner, pph: PPH, boundsRef, onStretch, onDrop: onMoveBooking || (() => {}) });
  const prevBefore = useRef(0);
  const unfreezeDrag = blockDrag.unfreeze;
  useLayoutEffect(() => {
    // Al crecer por arriba, el contenido baja: compensamos el scroll para que la cita
    // siga bajo el dedo (el hook ya ajustó startDocY) y liberamos el drag congelado.
    const diff = stretch.before - prevBefore.current;
    prevBefore.current = stretch.before;
    if (diff > 0) window.scrollBy(0, (diff / 60) * PPH);
    unfreezeDrag();
  }, [stretch.before, unfreezeDrag]);

  const posToMin = (clientY) => {
    const rect = layerRef.current.getBoundingClientRect();
    return ws + ((clientY - rect.top) / PPH) * 60;
  };

  const onPointerDown = (e) => {
    if (!isOwner || pressRef.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target.closest('[data-agenda-block]')) return;
    const m0 = posToMin(e.clientY);
    const press = { x0: e.clientX, y0: e.clientY, fired: false, timer: null };

    const endPress = (commit) => {
      if (press.timer) clearTimeout(press.timer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      document.body.style.userSelect = '';
      pressRef.current = null;
      const d = dragRef.current;
      setDragState(null);
      if (commit && press.fired && d) onSelectRange(d.start, d.end);
    };
    const onMove = (ev) => {
      if (!press.fired) {
        if (Math.abs(ev.clientY - press.y0) > 10 || Math.abs(ev.clientX - press.x0) > 10) endPress(false);
        return;
      }
      const d = dragRef.current;
      if (!d) return;
      // Founder 14-oct-2026: el bloque COMPLETO se mueve con el dedo conservando su duración
      // (antes se estiraba/encogía). La duración solo cambia con los tiradores de los bordes.
      const dur = d.end - d.start;
      const delta = posToMin(ev.clientY) - m0;
      const start = Math.max(ws, Math.min(Math.round((d.start0 + delta) / SNAP_MIN) * SNAP_MIN, we - dur));
      if (start !== d.start) setDragState({ ...d, start, end: start + dur });
    };
    const onUp = () => endPress(true);
    const onCancel = () => endPress(false);

    press.timer = setTimeout(() => {
      press.fired = true;
      document.body.style.userSelect = 'none';
      if (navigator.vibrate) { try { navigator.vibrate(15); } catch { /* noop */ } }
      const start = Math.max(ws, Math.min(snapFloor(m0), we - SNAP_MIN));
      const end = Math.min(start + 30, we);
      setDragState({ start, end, start0: start });
    }, 400);

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    pressRef.current = press;
  };

  useEffect(() => {
    const el = layerRef.current;
    if (!el || !isOwner) return undefined;
    // Sin esto el navegador inicia el scroll, dispara pointercancel y mata el gesto
    // (selección de rango, arrastre de cita o tirador de duración).
    const blockScroll = (e) => { if (pressRef.current?.fired || blockDrag.activeRef.current || resizeRef.current) e.preventDefault(); };
    el.addEventListener('touchmove', blockScroll, { passive: false });
    return () => el.removeEventListener('touchmove', blockScroll);
  }, [isOwner, blockDrag.activeRef]);

  // Tiradores (bordes superior/inferior) del bloque seleccionado: única forma de cambiar la duración.
  const resizeRef = useRef(false);
  const onHandleDown = (edge) => (e) => {
    if (!onResizeSelection || !selection) return;
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = true;
    document.body.style.userSelect = 'none';
    const base = { ...selection };
    const onMove = (ev) => {
      const m = Math.round(posToMin(ev.clientY) / SNAP_MIN) * SNAP_MIN;
      if (edge === 'top') {
        const start = Math.max(ws, Math.min(m, base.end - SNAP_MIN));
        if (start !== base.start) { base.start = start; onResizeSelection(start, base.end); }
      } else {
        const end = Math.min(we, Math.max(m, base.start + SNAP_MIN));
        if (end !== base.end) { base.end = end; onResizeSelection(base.start, end); }
      }
    };
    const onUp = () => {
      resizeRef.current = false;
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const sel = drag || selection;

  const hours = [];
  for (let h = Math.ceil(ws / 60) * 60; h <= we; h += 60) hours.push(h);

  const now = new Date();
  // Línea "ahora" con el reloj del NEGOCIO (business_today/business_now), no el del visitante.
  const isToday = selectedDay === (schedule.business_today || toLocalISO(now));
  const nowMin = schedule.business_now ? toMin(schedule.business_now) : now.getHours() * 60 + now.getMinutes();
  const bd = blockDrag.drag;
  const outBefore = isOwner && ws < wsCfg;
  const outAfter = isOwner && we > weCfg;

  return (
    <div data-testid="agenda-timeline">
      {!hideChrome && (
      <div
        className="flex items-center justify-center min-h-8 py-1.5 px-3 text-center rounded-lg border border-border/40 text-[10px] uppercase tracking-wider text-muted-foreground/50 mb-1"
        style={STRIPES}
        data-testid="agenda-out-of-hours"
      >
        {outBefore ? tr("Antes de {0}", formatTime12(toHHMM(ws))) : tr("Fuera de horario · antes de {0}", formatTime12(toHHMM(ws)))}
        {isOwner && ws > 0 && !bounds ? tr("· arrastra una cita hasta aquí para extender") : ''}
      </div>
      )}

      <div className="relative" style={{ height: totalH }}>
        {/* Deploy 3 · P3 — tramos fuera de la jornada configurada (solo owner) */}
        {outBefore && (
          <div className="absolute right-0 pointer-events-none flex items-start justify-end pr-2 pt-1" style={{ left: AX, top: 0, height: yOf(wsCfg), ...STRIPES }} data-testid="agenda-outside-before">
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground/50">{tr("Antes de abrir · {0}", formatTime12(schedule.work_start))}</span>
          </div>
        )}
        {outAfter && (
          <div className="absolute right-0 pointer-events-none flex items-end justify-end pr-2 pb-1" style={{ left: AX, top: yOf(weCfg), height: yOf(we) - yOf(weCfg), ...STRIPES }} data-testid="agenda-outside-after">
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground/50">{tr("Después de cerrar · {0}", formatTime12(schedule.work_end))}</span>
          </div>
        )}
        {/* Público — zona fuera de la jornada sin premium (rayada, no reservable) */}
        {publicStripes.map(([a, z]) => (
          <div key={`stripe-${a}`} className="absolute right-0 pointer-events-none" style={{ left: AX, top: yOf(a), height: yOf(z) - yOf(a), ...STRIPES }} data-testid={`agenda-public-outside-${toHHMM(a)}`} />
        ))}
        {hours.map((h) => (
          <div key={h} className="absolute left-0 right-0 flex items-start pointer-events-none" style={{ top: yOf(h) }}>
            {!hideAxis && (
            <span className="w-[68px] pr-2 text-right text-[10px] text-muted-foreground -mt-1.5 tabular-nums shrink-0">
              {formatTime12(toHHMM(h))}
            </span>
            )}
            <div className="flex-1 border-t border-border/40" />
          </div>
        ))}

        {/* Interactive layer */}
        <div
          ref={layerRef}
          className="absolute inset-y-0 right-0 cursor-pointer select-none"
          style={{ left: AX }}
          onClick={onGridTap}
          onPointerDown={onPointerDown}
          onContextMenu={(e) => { if (isOwner) e.preventDefault(); }}
          data-testid="agenda-grid"
        >
          {/* Público — bandas Horario Premium (inicio/fin exactos + recargo visible antes de tocar) */}
          {premiumBands.map((b) => {
            const top = yOf(b.startMin);
            const height = Math.max(24, yOf(b.endMin) - top);
            const labelTop = yOf(b.labelMin) - top;
            return (
              <div
                key={`prem-${b.id}-${b.startMin}`}
                className="absolute left-1 right-1 rounded-lg border border-dashed border-primary/60 bg-primary/[0.07] pointer-events-none overflow-hidden z-[2]"
                style={{ top, height }}
                data-testid={`agenda-premium-band-${toHHMM(b.startMin)}`}
                data-available={b.available}
              >
                <div className="absolute left-0 right-0" style={{ top: labelTop }}>
                  <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary min-w-0">
                      <Sparkles className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{tr("Horario Premium")}</span>
                    </span>
                    <span className="shrink-0 rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 tabular-nums" data-testid={`agenda-premium-label-${toHHMM(b.startMin)}`}>
                      {b.label}
                    </span>
                  </div>
                  {height >= 44 && (
                    <p className="px-2.5 -mt-1 text-[10px] text-primary/80 tabular-nums" data-testid={`agenda-premium-range-${toHHMM(b.startMin)}`}>
                      {formatTime12(toHHMM(b.startMin))} – {formatTime12(toHHMM(b.endMin))}
                      {b.available > 0 ? tr("· toca para solicitar") : tr("· sin horarios libres")}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {blocks.map((b) => {
            const dragging = Boolean(bd) && b.type === 'busy' && Boolean(b.booking_id) && bd.id === b.booking_id;
            const startMin = dragging ? bd.newStart : b.startMin;
            const endMin = dragging ? bd.newStart + (b.endMin - b.startMin) : b.endMin;
            const top = yOf(Math.max(startMin, ws));
            // Altura real; el mínimo legible (20 px) nunca pisa el bloque siguiente.
            const nextStart = blocks.reduce((acc, o) => (o !== b && o.startMin > startMin && o.startMin < acc ? o.startMin : acc), Infinity);
            const minH = Number.isFinite(nextStart) ? Math.min(20, Math.max(8, yOf(nextStart) - top)) : 20;
            const height = Math.max(minH, yOf(Math.min(endMin, we)) - top);
            const range = `${formatTime12(toHHMM(startMin))} – ${formatTime12(toHHMM(endMin))}`;
            const compact = height < 40;
            if (b.type === 'busy') {
              const outsideShop = isOwner && b.outside_shop_hours && !['cancelled', 'no_show', 'completed'].includes(b.status);
              // 1C — color del servicio como fondo (solo el dueño/gestor lo ve); el estado va por borde/icono/opacidad.
              const colored = isOwner ? bookingBlockStyle(b.service_color, b.status) : null;
              const st = colored
                ? `${outsideShop ? 'border-dashed' : ''} ${b.status === 'completed' ? 'ring-2 ring-emerald-400 ring-inset' : ''} ${b.status === 'cancelled' ? 'line-through' : ''}`
                : outsideShop
                ? 'border-l-amber-400 border-amber-400/50 border-dashed bg-amber-500/10 hover:bg-amber-500/15'
                : {
                  no_show: 'border-l-amber-500 border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/15',
                  completed: 'border-l-emerald-500 border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/15',
                  cancelled: 'border-l-destructive border-destructive/30 bg-destructive/10 hover:bg-destructive/15',
                }[b.status] || 'border-l-primary border-primary/25 bg-primary/10 hover:bg-primary/15';
              const txt = colored ? '' : 'text-foreground';
              const sub = colored ? '' : 'text-muted-foreground';
              const draggable = isOwner && canDragBooking(b);
              return (
                <button
                  key={`busy-${b.booking_id || b.startMin}`}
                  type="button"
                  data-agenda-block=""
                  data-draggable={draggable ? 'true' : 'false'}
                  data-dragging={dragging ? 'true' : undefined}
                  data-service-color={b.service_color || undefined}
                  onPointerDown={(e) => { if (draggable) blockDrag.onBlockPointerDown(b, e); }}
                  onContextMenu={(e) => { if (draggable) e.preventDefault(); }}
                  onClick={(e) => { e.stopPropagation(); if (blockDrag.consumeTap()) return; onBookingTap(b); }}
                  style={{ top, height, touchAction: dragging ? 'none' : 'pan-y', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', ...(colored || {}),
                    ...(lanes.get(blocks.indexOf(b))?.n > 1 && !dragging ? { left: `calc(4px + ${(100 / lanes.get(blocks.indexOf(b)).n) * lanes.get(blocks.indexOf(b)).lane}%)`, width: `calc(${100 / lanes.get(blocks.indexOf(b)).n}% - 6px)`, right: 'auto' } : {}) }}
                  data-lane={lanes.get(blocks.indexOf(b))?.n > 1 ? `${lanes.get(blocks.indexOf(b)).lane + 1}/${lanes.get(blocks.indexOf(b)).n}` : undefined}
                  className={`absolute left-1 right-1 rounded-lg border-l-[3px] border px-2.5 overflow-hidden text-left select-none transition-[box-shadow,transform,background-color] ${st} ${b.status === 'no_show' ? 'opacity-55' : ''} ${compact ? 'py-0.5' : 'py-1.5'} ${dragging ? `z-30 ring-2 ring-primary shadow-2xl scale-[1.02] cursor-grabbing ${colored ? '' : 'bg-primary/20'}` : 'z-[5]'}`}
                  data-testid={`agenda-seg-busy-${toHHMM(b.startMin)}`}
                >
                  {/* Badges esquina sup. derecha — no asistió / pagada ($) */}
                  {isOwner && b.status === 'no_show' ? (
                    <span
                      className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-red-500 flex items-center justify-center pointer-events-none shadow"
                      data-testid={`agenda-noshow-badge-${b.booking_id}`}>
                      <UserX className="w-2 h-2 text-white" />
                    </span>
                  ) : isOwner && b.payment_status === 'paid' ? (
                    <span
                      className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 flex items-center justify-center pointer-events-none shadow"
                      data-testid={`agenda-paid-badge-${b.booking_id}`}>
                      <DollarSign className="w-2 h-2 text-white" strokeWidth={3} />
                    </span>
                  ) : null}
                  {outsideShop && (
                    <span
                      className={`absolute top-0.5 ${b.payment_status === 'paid' ? 'right-4' : 'right-0.5'} w-3.5 h-3.5 rounded-full bg-amber-400 flex items-center justify-center pointer-events-none shadow`}
                      title={tr("Fuera del horario del local")}
                      data-testid={`agenda-outside-shop-badge-${b.booking_id}`}>
                      <MoonStar className="w-2 h-2 text-black" strokeWidth={3} />
                    </span>
                  )}
                  {colored && b.status === 'completed' && (
                    <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 flex items-center justify-center pointer-events-none shadow"
                      data-testid={`agenda-completed-badge-${b.booking_id}`}>
                      <Check className="w-2 h-2 text-white" strokeWidth={3} />
                    </span>
                  )}
                  <span className={`block text-xs font-semibold ${txt} truncate pr-4`}>
                    {compact && !dragging && (
                      <span className={`font-normal text-[10px] ${sub} tabular-nums mr-1`} style={colored ? { color: 'var(--block-muted)' } : undefined} data-testid={`agenda-seg-busy-start-${toHHMM(b.startMin)}`}>{formatTime12(toHHMM(startMin))}</span>
                    )}
                    {isOwner && (b.series_id || b.recurring) && <Repeat className="inline w-3 h-3 text-primary/80 mr-1 align-[-2px]" aria-label={tr("Se repite")} data-testid={`agenda-seg-repeat-${toHHMM(b.startMin)}`} />}
                    {isOwner ? (b.client_name || tr("Cliente")) : tr("Reservado")}
                    {b.service_name ? ` · ${b.service_name}` : ''}

                    {showPrice && b.price != null && b.status !== 'cancelled' && (
                      <span className={`font-display font-bold ${colored ? '' : 'text-primary'} ml-1`} data-testid={`agenda-seg-price-${toHHMM(b.startMin)}`}>{fmtMoney(b.price)}</span>
                    )}
                  </span>
                  {dragging ? (
                    <span className={`block text-[10px] font-mono font-bold ${colored ? '' : 'text-primary'} tabular-nums`} data-testid="agenda-drag-preview">{range}</span>
                  ) : (!compact && <span className={`block text-[10px] ${sub} tabular-nums truncate`} style={colored ? { color: 'var(--block-muted)' } : undefined}>
                    {isOwner && b.forced_badge && (
                      <span className="mr-1.5 align-[1px] inline-block px-1 rounded text-[8px] leading-[12px] font-semibold uppercase tracking-wide bg-black/55 text-white"
                        data-testid={`agenda-forced-badge-${b.booking_id}`}>{tr(b.forced_badge)}</span>
                    )}
                    {range}
                  </span>)}
                </button>
              );
            }
            const tappable = isOwner && onBlockTap && b.id;
            const blockTap = (e) => { e.stopPropagation(); if (tappable) onBlockTap(b); };
            const repeatIcon = (b.recurring || b.series_id) ? <Repeat className="w-3 h-3 text-primary/80" aria-label={tr("Se repite")} data-testid={`agenda-seg-repeat-${toHHMM(b.startMin)}`} /> : null;
            if (b.type === 'break') {
              return (
                <div
                  key={`break-${b.id || b.startMin}`}
                  data-agenda-block=""
                  style={{ top, height }}
                  onClick={blockTap}
                  role={tappable ? 'button' : undefined}
                  className={`absolute left-1 right-1 rounded-lg border border-dashed border-border bg-muted/30 px-2.5 overflow-hidden ${compact ? 'py-0.5' : 'py-1.5'} ${tappable ? 'cursor-pointer hover:border-primary/50' : ''}`}
                  data-testid={`agenda-seg-break-${toHHMM(b.startMin)}`}
                >
                  <span className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1.5">
                    <Coffee className="w-3 h-3" />{' '}{b.name || tr("Descanso")}{repeatIcon}</span>
                  {!compact && <span className="block text-[10px] text-muted-foreground/70 tabular-nums">{range}</span>}
                </div>
              );
            }
            return (
              <div
                key={`blocked-${b.id || b.startMin}`}
                data-agenda-block=""
                style={{ top, height, ...STRIPES }}
                onClick={blockTap}
                role={tappable ? 'button' : undefined}
                className={`absolute left-1 right-1 rounded-lg border border-border/60 bg-background px-2.5 overflow-hidden ${compact ? 'py-0.5' : 'py-1.5'} ${tappable ? 'cursor-pointer hover:border-primary/50' : ''}`}
                data-testid={`agenda-seg-blocked-${toHHMM(b.startMin)}`}
              >
                <span className="text-xs font-medium text-muted-foreground/60 inline-flex items-center gap-1.5">{isOwner && b.name ? b.name : tr("No disponible")}{repeatIcon}</span>
                {!compact && <span className="block text-[10px] text-muted-foreground/50 tabular-nums">{range}</span>}
              </div>
            );
          })}

          {/* Owner: bloque temporal de selección (se mueve con el dedo; tiradores para la duración) */}
          {sel && (
            <div
              className="absolute left-1 right-1 rounded-lg border-2 border-dashed border-primary bg-primary/15 z-20 pointer-events-none flex flex-col justify-center px-2.5"
              style={{ top: yOf(sel.start), height: Math.max(24, yOf(sel.end) - yOf(sel.start)) }}
              data-testid="agenda-selection-block"
            >
              <span className="text-xs font-bold text-primary tabular-nums" data-testid="agenda-selection-range">
                {formatTime12(toHHMM(sel.start))} – {formatTime12(toHHMM(sel.end))}
              </span>
              {yOf(sel.end) - yOf(sel.start) >= 44 && (
                <span className="text-[10px] text-primary/70">{drag ? tr("{0} min · desliza para mover", sel.end - sel.start) : tr("{0} min · arrastra los bordes para ajustar", sel.end - sel.start)}</span>
              )}
              {!drag && selection && onResizeSelection && (
                <>
                  <div onPointerDown={onHandleDown('top')} className="absolute -top-3 left-0 right-0 h-6 flex items-start justify-center pointer-events-auto cursor-ns-resize" style={{ touchAction: 'none' }} data-testid="agenda-selection-handle-top">
                    <div className="mt-1.5 w-10 h-3 rounded-full bg-primary shadow-md border-2 border-background" />
                  </div>
                  <div onPointerDown={onHandleDown('bottom')} className="absolute -bottom-3 left-0 right-0 h-6 flex items-end justify-center pointer-events-auto cursor-ns-resize" style={{ touchAction: 'none' }} data-testid="agenda-selection-handle-bottom">
                    <div className="mb-1.5 w-10 h-3 rounded-full bg-primary shadow-md border-2 border-background" />
                  </div>
                </>
              )}
            </div>
          )}
          {!drag && selection && selectionMenu && (
            <div className="absolute left-1 right-1 z-30" style={{ top: yOf(selection.end) + 14 }} data-testid="agenda-selection-menu">
              {selectionMenu}
            </div>
          )}

          {/* Now line */}
          {isToday && nowMin >= ws && nowMin <= we && (
            <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: yOf(nowMin) }} data-testid="agenda-now-line">
              <div className="h-px bg-primary" style={{ boxShadow: '0 0 8px hsl(45 90% 55% / 0.6)' }} />
              <div className="absolute -left-0.5 -top-[3px] w-1.5 h-1.5 rounded-full bg-primary" />
            </div>
          )}
        </div>
      </div>

      {!hideChrome && (
      <div
        className="flex items-center justify-center min-h-8 py-1.5 px-3 text-center rounded-lg border border-border/40 text-[10px] uppercase tracking-wider text-muted-foreground/50 mt-1"
        style={STRIPES}
        data-testid="agenda-out-of-hours-after"
      >
        {outAfter ? tr("Después de {0}", formatTime12(toHHMM(we))) : tr("Fuera de horario · después de {0}", formatTime12(toHHMM(we)))}
        {isOwner && we < 1440 && !bounds ? tr("· arrastra una cita hasta aquí para extender") : ''}
      </div>
      )}
    </div>
  );
};

// ── Owner booking detail sheet (full data + actions + reschedule) ──
// F2 (oct-2026) — `manager`: vista de la barbería (crear/mover/cancelar + contacto del cliente; NUNCA dinero).
export const BookingDetailSheet = ({
  booking, barber, date, open, onOpenChange, actionBusy,
  onComplete, onNoShow, onCancel, onMessage, onRescheduled, onCharge,
  onResendReceipt, onRefund, onEdit = null, manager = false, onReload = null,
}) => {
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedDay, setReschedDay] = useState(date);
  const [reschedSlots, setReschedSlots] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feeArm, setFeeArm] = useState(false);

  useEffect(() => {
    if (open) { setReschedOpen(false); setReschedDay(date); setReschedSlots(null); setFeeArm(false); }
  }, [open, date]);

  useEffect(() => {
    if (!reschedOpen || !booking) return;
    let cancelled = false;
    setReschedSlots(null);
    (async () => {
      try {
        const res = await bookingApi.availability(barber.username, reschedDay, booking.service_id || undefined, booking.booking_id);
        if (cancelled) return;
        const todayISO = toLocalISO(new Date());
        const now = new Date();
        const nowHHMM = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        setReschedSlots((res?.slots || []).filter((t) => reschedDay !== todayISO || t > nowHHMM));
      } catch {
        if (!cancelled) setReschedSlots([]);
      }
    })();
    return () => { cancelled = true; };
  }, [reschedOpen, reschedDay, booking, barber.username]);

  const reschedDays = useMemo(() => Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  }), []);

  const doReschedule = async (time) => {
    if (saving) return;
    setSaving(true);
    try {
      await bookingApi.rescheduleBooking(booking.booking_id, reschedDay, time);
      toast.success(tr("Cita movida a {0} · {1}", formatDateShortEs(reschedDay), formatTime12(time)));
      onRescheduled();
    } catch (err) {
      toast.error(err?.message || tr("No pudimos reprogramar la cita"));
    } finally {
      setSaving(false);
    }
  };

  const doChargeFee = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await bookingApi.chargeFee(booking.booking_id);
      toast.success(tr("Cargo de ${0} cobrado a la tarjeta del cliente", Number(res?.fee_charged?.amount || 0).toFixed(2)));
      onRescheduled();
    } catch (err) {
      toast.error(err?.message || tr("No pudimos procesar el cargo"));
    } finally {
      setSaving(false);
      setFeeArm(false);
    }
  };

  // B7 (founder 14-oct-2026) — «El cliente llegó»: deshacer inasistencia hasta el final del día de la cita.
  const [undoAsk, setUndoAsk] = useState(false);
  const doUndoNoShow = async (refundFee) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await bookingApi.undoNoShow(booking.booking_id, refundFee);
      toast.success(res?.fee_refunded
        ? tr("Inasistencia revertida · reembolso de ${0} al cliente", Number(res.fee_refunded).toFixed(2))
        : tr("Inasistencia revertida · la cita vuelve a estar activa"));
      setUndoAsk(false);
      onRescheduled();
    } catch (err) {
      toast.error(err?.message || tr("No pudimos revertir la inasistencia"));
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return null;
  // D5 — cita con dinero cobrado: "Cancelar" pasa por el reembolso (Stripe) o la anulación (efectivo).
  const isPaid = ['paid', 'deposit_paid'].includes(booking.payment_status);
  const canUndoNoShow = !manager && booking.status === 'no_show' && (booking.date || date) === (booking.business_today || date);
  const isFinal = ['completed', 'no_show', 'cancelled'].includes(booking.status);
  const canRefund = isPaid && booking.status !== 'cancelled';
  const editedAfterPayment = booking.time_edited_after_payment;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" aria-describedby={undefined} className="bg-card border-border rounded-t-2xl max-h-[85vh] overflow-y-auto" data-testid="booking-detail-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-lg text-foreground flex items-center gap-2">
            <Scissors className="w-4 h-4 text-primary" />{' '}{tr("Detalle de la cita")}</SheetTitle>
        </SheetHeader>

        <div className="mt-3 space-y-4">
          {/* Cliente */}
          <div className="flex items-center gap-3 bg-background border border-border rounded-xl p-3">
            <Avatar className="w-10 h-10 border border-primary/30">
              <AvatarImage src={booking.client_avatar} alt={booking.client_name} />
              <AvatarFallback>{(booking.client_name || '?')[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground text-sm truncate" data-testid="detail-client-name">{booking.client_name}</p>
              <p className="text-xs text-muted-foreground truncate">{booking.client_username ? `@${booking.client_username}` : tr("Cliente sin cuenta")}</p>
            </div>
            {(() => {
              const map = {
                pending: [tr("Pendiente"), 'border-primary/50 text-primary'],
                confirmed: [tr("Confirmada"), 'border-primary/50 text-primary'],
                completed: [tr("Completada"), 'border-emerald-500/50 text-emerald-400'],
                no_show: [tr("No asistió"), 'border-amber-500/50 text-amber-400'],
                cancelled: [tr("Cancelada"), 'border-destructive/50 text-destructive'],
              };
              const [label, cls] = map[booking.status] || map.confirmed;
              return (
                <Badge variant="outline" className={`${cls} text-[9px] uppercase tracking-wider shrink-0`} data-testid="detail-status-badge">
                  {label}
                </Badge>
              );
            })()}
          </div>

          {/* F2 — contacto del cliente (atención y campañas) */}
          {(booking.client_phone || booking.client_email) && (
            <div className="bg-background border border-border rounded-xl p-3 space-y-1.5 text-sm" data-testid="detail-contact-section">
              {booking.client_phone && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-muted-foreground text-xs">{tr("Teléfono")}</span>
                  <a href={`tel:${booking.client_phone}`} className="font-medium text-sm text-primary underline-offset-2 hover:underline" data-testid="detail-client-phone">{booking.client_phone}</a>
                </div>
              )}
              {booking.client_email && (
                <div className="flex justify-between items-center gap-2 min-w-0">
                  <span className="text-muted-foreground text-xs">{tr("Correo")}</span>
                  <a href={`mailto:${booking.client_email}`} className="font-medium text-sm text-primary truncate underline-offset-2 hover:underline" data-testid="detail-client-email">{booking.client_email}</a>
                </div>
              )}
            </div>
          )}

          {/* Cita */}
          <div className="bg-background border border-border rounded-xl p-3 space-y-1.5 text-sm">
            {manager && <Row label={tr("Barbero")} value={barber?.name || barber?.username} testId="detail-barber-name" />}
            <Row label={tr("Servicio")} value={booking.service_name} />
            {booking.services?.length > 1 && (
              <ul className="pl-1 space-y-0.5" data-testid="detail-service-lines">
                {booking.services.map((l) => (
                  <li key={l.id} className="flex justify-between gap-3 text-xs text-muted-foreground" data-testid={`detail-service-line-${l.id}`}>
                    <span className="truncate">· {l.name} · {formatDuration(l.duration)}</span><span className="tabular-nums">${Number(l.price).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            )}
            <Row label={tr("Horario")} value={`${formatTime12(booking.start)} – ${formatTime12(booking.end)}`} />
            <Row label={tr("Duración")} value={formatDuration(booking.duration_minutes)} />
            {booking.price != null && <Row label={tr("Precio")} value={fmtMoney(booking.price)} gold testId="detail-price-row" />}
            {booking.premium && (
              <Row label={tr("Horario premium")} value={booking.premium.surcharge != null ? `${booking.premium.label} · +${fmtMoney(booking.premium.surcharge)}` : booking.premium.label} testId="detail-premium-row" />
            )}
            {Number(booking.premium_surcharge_manual) > 0 && (
              <Row label={tr("Recargo fuera de horario")} value={`+${fmtMoney(booking.premium_surcharge_manual)}`} testId="detail-surcharge-row" />
            )}
            {booking.outside_hours && !booking.premium && !isFinal && (
              <p className="text-[11px] text-amber-300 inline-flex items-center gap-1 pt-1" data-testid="detail-outside-hours-flag">
                <MoonStar className="w-3 h-3" />{' '}{manager ? tr("Fuera de la jornada del barbero") : tr("Fuera de tu jornada — al cobrar podrás añadir un recargo")}</p>
            )}
            {booking.outside_shop_hours && !isFinal && (
              <p className="text-[11px] text-amber-300 inline-flex items-start gap-1 pt-1" data-testid="detail-outside-shop-flag">
                <MoonStar className="w-3 h-3 shrink-0 mt-px" />{' '}{manager ? tr("Fuera del horario del local — la cita se respeta; coordínala con el barbero o muévela.") : tr("Fuera del horario del local — la cita se respeta; coordínala con tu barbería o muévela.")}</p>
            )}
            {booking.note && (
              <p className="text-xs text-muted-foreground italic pt-1.5 border-t border-border mt-1.5">“{booking.note}”</p>
            )}
          </div>

          {/* D5 · B — trazabilidad: horario editado después del cobro */}
          {editedAfterPayment && (
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-3 text-xs space-y-0.5" data-testid="detail-time-edited">
              <p className="font-semibold text-amber-300 inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" />{' '}{tr("Horario editado tras el cobro")}</p>
              {editedAfterPayment.from?.start_time && (
                <p className="text-foreground/75">{tr("Antes: {0} · {1} {2}", formatDateShortEs(editedAfterPayment.from.date), formatTime12(editedAfterPayment.from.start_time), editedAfterPayment.count > 1 ? tr("· {0} ediciones", editedAfterPayment.count) : '')}</p>
              )}
              <p className="text-muted-foreground">{tr("El cobro, el reparto y Mi Dinero no cambiaron; la fecha real de pago se conserva en el recibo.")}</p>
            </div>
          )}

          {/* Pago (Iter 195) */}
          {booking.payment_status && (
            <div className="bg-background border border-border rounded-xl p-3 space-y-1.5 text-sm" data-testid="detail-payment-section">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">{tr("Estado de pago")}</span>
                <Badge
                  variant="outline"
                  className={`text-[9px] uppercase tracking-wider ${
                    booking.payment_status === 'paid'
                      ? 'border-emerald-500/50 text-emerald-400'
                      : booking.payment_status === 'deposit_paid'
                        ? 'border-primary/50 text-primary'
                        : 'border-border text-muted-foreground'
                  }`}
                  data-testid="detail-payment-status"
                >
                  {{ paid: tr("Pagada"), deposit_paid: tr("Depósito pagado"), unpaid: tr("Sin pago"),
                     pending: tr("Pago pendiente"), payment_failed: tr("Pago fallido"),
                     refunded: tr("Reembolsada"), partially_refunded: tr("Reembolso parcial") }[booking.payment_status] || booking.payment_status}
                </Badge>
              </div>
              {Number(booking.amount_paid) > 0 && (
                <>
                  <Row label={tr("Pagado")} value={fmtMoney(booking.amount_paid)} gold />
                  <Row label={tr("Saldo pendiente")} value={fmtMoney(booking.remaining_balance)} />
                </>
              )}
              {booking.payment_method_last4 && (
                <Row label={tr("Método")} value={`${(booking.payment_method_brand || tr("Tarjeta")).toUpperCase()} ···· ${booking.payment_method_last4}`} />
              )}
              {booking.payment_method === 'cash' && <Row label={tr("Método")} value="Efectivo" />}
              {booking.payment_status === 'refunded' && !manager && (
                <Row label={booking.refund_kind === 'cash_void' ? tr("Cobro anulado") : tr("Reembolsado al cliente")}
                  value={booking.refund_kind === 'cash_void' ? tr("Sí (efectivo)") : `${fmtMoney(booking.refund_amount)} ${tr("(neto)")}`} testId="detail-refund-row" />
              )}
              {booking.prepaid && booking.payment_status === 'paid' && !manager && (
                <p className="text-[11px] text-emerald-400 pt-1 inline-flex items-center gap-1" data-testid="detail-prepaid-note">
                  <CheckCircle2 className="w-3 h-3" />{' '}{tr("Pagada por adelantado por el cliente{0} — solo marca la cita como completada, sin cobrar.", Number(booking.tip_amount) > 0 ? tr("· propina ${0}", Number(booking.tip_amount).toFixed(2)) : '')}</p>
              )}
              {booking.late_cancel_kept && (
                <p className="text-[11px] text-amber-300 pt-1" data-testid="detail-late-cancel-kept">{tr("Cancelación tardía — el pago quedó retenido y se reparte como cita cobrada.")}</p>
              )}
            </div>
          )}

          {/* Link de pago por correo (Cobrar): estado + Reenviar también desde el detalle de la cita. */}
          {!manager && booking.pay_link && <div className="mb-2"><PayLinkStatus booking={booking} onChanged={() => onReload?.()} /></div>}
          {/* Cobrar (Fase 1 — venta). Acción principal: va antes de las secundarias
              para que sea visible sin scroll a 390×844 (bug de uso reportado por el founder, 27-sep). */}
          {!manager && booking.payment_status !== 'paid' && booking.payment_status !== 'refunded' && (
            <Button
              onClick={() => onCharge(booking)}
              disabled={actionBusy || saving}
              className="w-full h-10 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 font-semibold"
              data-testid="detail-charge-btn"
            >
              <HandCoins className="w-4 h-4" />{' '}{tr("Cobrar")}</Button>
          )}

          {/* Acciones — una cita PAGADA POR ADELANTADO sigue necesitando «Completada» (sin cobro nuevo). */}
          {manager && (
            <p className="text-[11px] text-muted-foreground" data-testid="detail-manager-note">{tr("Vista de la barbería: puedes mover o cancelar esta cita y contactar al cliente. Cobros, completar y dinero son del barbero.")}</p>
          )}
          <div className="flex items-center gap-2">
            {manager ? null : (booking.status === 'completed' || (isFinal && booking.payment_status === 'paid')) ? (
              <Button
                onClick={() => onResendReceipt(booking)}
                disabled={actionBusy || saving}
                variant="outline"
                size="sm"
                className="h-9 rounded-full border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 gap-1 text-xs flex-1"
                data-testid="detail-resend-receipt-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />{' '}{tr("Reenviar recibo")}</Button>
            ) : (
              <Button
                onClick={() => onComplete(booking)}
                disabled={actionBusy || saving}
                variant="outline"
                size="sm"
                className="h-9 rounded-full border-primary/50 text-primary hover:bg-primary/10 gap-1 text-xs flex-1"
                data-testid="detail-complete-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />{' '}{tr("Completada")}</Button>
            )}
            {!isFinal && !manager && (
              <Button
                onClick={() => onNoShow(booking)}
                disabled={actionBusy || saving}
                variant="outline"
                size="sm"
                className="h-9 rounded-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10 gap-1 text-xs flex-1"
                data-testid="detail-noshow-btn"
              >
                <UserX className="w-3.5 h-3.5" />{' '}{tr("No asistió")}</Button>
            )}
            {canRefund && !manager ? (
              <Button
                onClick={() => onRefund(booking)}
                disabled={actionBusy || saving}
                variant="outline"
                size="sm"
                className="h-9 rounded-full border-destructive/50 text-destructive hover:bg-destructive/10 gap-1 text-xs flex-1"
                data-testid="detail-refund-btn"
              >
                <RotateCcw className="w-3.5 h-3.5" /> {booking.payment_method === 'cash' ? tr("Anular cobro") : tr("Reembolsar")}
              </Button>
            ) : !isFinal && (
              <Button
                onClick={() => onCancel(booking)}
                disabled={actionBusy || saving}
                variant="outline"
                size="sm"
                className="h-9 rounded-full border-destructive/50 text-destructive hover:bg-destructive/10 gap-1 text-xs flex-1"
                data-testid="detail-cancel-btn"
              >
                <XCircle className="w-3.5 h-3.5" />{' '}{tr("Cancelar")}</Button>
            )}
            {booking.client_username && (
              <Button
                onClick={() => onMessage(booking)}
                variant="outline"
                size="sm"
                className="h-9 w-9 p-0 rounded-full border-border shrink-0"
                data-testid="detail-message-btn"
                aria-label={tr("Enviar mensaje al cliente")}
              >
                <MessageSquare className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          {/* Fase B — cargo por no-show / cancelación tardía (manual) */}
          {!manager && booking.card_hold?.consent_id && !booking.fee_charged
            && ['no_show', 'cancelled'].includes(booking.status) && (
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-3 space-y-2" data-testid="detail-fee-section">
              <p className="text-xs text-foreground/80">
                {booking.status === 'no_show'
                  ? tr("Cliente no asistió — puedes cobrar ${0} a su tarjeta guardada (aceptó tu política al reservar).", Number(booking.card_hold.no_show_fee || 0).toFixed(2))
                  : booking.late_cancel
                    ? tr("Cancelación tardía (dentro de la ventana de {0} h que aceptó) — puedes cobrar ${1} a su tarjeta guardada.", booking.late_cancel_window_hours ?? booking.card_hold.window_hours, Number(booking.card_hold.late_cancel_fee || 0).toFixed(2))
                    : tr("Cancelación con garantía — si fue tardía puedes cobrar ${0} a su tarjeta guardada.", Number(booking.card_hold.late_cancel_fee || 0).toFixed(2))}
              </p>
              {booking.fee_auto_charge?.outcome === 'declined' && (
                <p className="text-[11px] text-amber-300" data-testid="detail-fee-auto-declined">{tr("El cobro automático fue rechazado por la tarjeta del cliente. Puedes reintentarlo aquí.")}</p>
              )}
              <Button
                onClick={() => (feeArm ? doChargeFee() : setFeeArm(true))}
                disabled={actionBusy || saving}
                size="sm"
                className={`w-full h-9 rounded-full gap-1.5 text-xs font-semibold ${feeArm ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground' : 'bg-amber-500/90 hover:bg-amber-500 text-black'}`}
                data-testid="detail-charge-fee-btn"
              >
                <HandCoins className="w-3.5 h-3.5" />
                {feeArm ? tr("Confirmar cargo — esto cobra dinero real") : tr("Cobrar cargo a tarjeta guardada")}
              </Button>
            </div>
          )}
          {canUndoNoShow && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="detail-undo-no-show">
              <p className="text-[11px] text-amber-200">{tr("¿El cliente sí llegó? Puedes revertir la inasistencia hasta el final de hoy: la cita vuelve a su estado anterior y se puede cobrar normal.")}</p>
              {!undoAsk ? (
                <Button onClick={() => (booking.fee_charged && !booking.fee_charged.refunded ? setUndoAsk(true) : doUndoNoShow(false))} disabled={saving}
                  variant="outline" className="w-full h-10 rounded-full border-amber-500/50 text-amber-300 hover:bg-amber-500/10 gap-1.5" data-testid="detail-undo-no-show-btn">
                  <RotateCcw className="w-4 h-4" />{tr("El cliente llegó")}</Button>
              ) : (
                <div className="space-y-1.5" data-testid="detail-undo-no-show-refund-ask">
                  <p className="text-[11px] text-foreground">{tr("Se cobró ${0} por inasistencia. ¿Reembolsarlo al cliente?", Number(booking.fee_charged.amount).toFixed(2))}</p>
                  <Button onClick={() => doUndoNoShow(true)} disabled={saving} className="w-full h-10 rounded-full gap-1.5" data-testid="detail-undo-no-show-refund-yes">
                    <RotateCcw className="w-4 h-4" />{tr("Sí, reembolsar y revertir")}</Button>
                  <Button onClick={() => doUndoNoShow(false)} disabled={saving} variant="outline" className="w-full h-10 rounded-full border-border" data-testid="detail-undo-no-show-refund-no">
                    {tr("Revertir sin reembolsar")}</Button>
                  <Button onClick={() => setUndoAsk(false)} disabled={saving} variant="ghost" className="w-full h-9 rounded-full text-muted-foreground" data-testid="detail-undo-no-show-back">{tr("Atrás")}</Button>
                </div>
              )}
            </div>
          )}
          {booking.no_show_undone_at && !manager && (
            <p className="text-[11px] text-muted-foreground" data-testid="detail-no-show-undone">{tr("Inasistencia revertida por {0} · {1}", booking.no_show_undone_by_name || tr("el barbero"), new Date(booking.no_show_undone_at).toLocaleString(localeTag(), { dateStyle: 'short', timeStyle: 'short' }))}</p>
          )}
          {booking.fee_charged && !manager && (
            <p className="text-[11px] text-emerald-400" data-testid="detail-fee-charged">{tr("✓ Cargo de ${0} cobrado ({1}{2})", Number(booking.fee_charged.amount).toFixed(2), booking.fee_charged.fee_type === 'no_show' ? 'inasistencia' : tr("cancelación tardía"), booking.fee_charged.auto ? tr("· automático") : '')}</p>
          )}

          {/* Reprogramar (D5 · B: una completada solo cambia fecha/hora; el dinero no se toca) */}
          {booking.status === 'completed' && !reschedOpen && !manager && (
            <p className="text-[11px] text-muted-foreground -mb-2" data-testid="detail-completed-move-hint">{tr("Cita completada: puedes corregir su fecha/hora (arrástrala en la agenda o usa Reprogramar). El cobro y Mi Dinero no cambian.")}</p>
          )}
          {onEdit && !reschedOpen && !['cancelled', 'no_show'].includes(booking.status) && !(manager && booking.status === 'completed') && (
            <Button onClick={() => onEdit(booking)} className="w-full h-10 rounded-full gap-1.5" data-testid="detail-edit-btn">
              <PenLine className="w-4 h-4" /> {tr("Editar")}
            </Button>
          )}
          {!reschedOpen && !['cancelled', 'no_show'].includes(booking.status) && !(manager && booking.status === 'completed') ? (
            <Button
              onClick={() => setReschedOpen(true)}
              variant="outline"
              className="w-full h-10 rounded-full border-primary/50 text-primary hover:bg-primary/10 gap-1.5"
              data-testid="detail-reschedule-btn"
            >
              <CalendarClock className="w-4 h-4" /> {booking.status === 'completed' ? tr("Editar fecha/hora") : tr("Reprogramar cita")}
            </Button>
          ) : reschedOpen && (
            <div className="bg-background border border-primary/25 rounded-xl p-3" data-testid="reschedule-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-foreground inline-flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5 text-primary" />{' '}{tr("Nuevo horario")}</p>
                <button
                  onClick={() => setReschedOpen(false)}
                  className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-0.5"
                  data-testid="reschedule-back"
                >
                  <ChevronLeft className="w-3 h-3" />{' '}{tr("Volver")}</button>
              </div>
              <div className="-mx-1 overflow-x-auto no-scrollbar mb-2.5">
                <div className="flex items-center gap-1.5 px-1 w-max min-w-full">
                  {reschedDays.map((d) => {
                    const iso = toLocalISO(d);
                    const active = iso === reschedDay;
                    return (
                      <button
                        key={iso}
                        type="button"
                        onClick={() => setReschedDay(iso)}
                        className={`flex flex-col items-center justify-center min-w-[44px] px-1.5 py-1.5 rounded-lg border transition-colors shrink-0 ${
                          active ? 'bg-primary/15 border-primary/60 text-primary' : 'bg-card border-border text-muted-foreground'
                        }`}
                        data-testid={`reschedule-day-${iso}`}
                      >
                        <span className="text-[9px] uppercase tracking-wider font-semibold">
                          {d.toLocaleDateString(localeTag(), { weekday: 'short' }).replace('.', '')}
                        </span>
                        <span className="font-display text-sm font-bold leading-tight">{d.getDate()}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {reschedSlots === null ? (
                <div className="py-4 text-center">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : reschedSlots.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3" data-testid="reschedule-no-slots">{tr("No hay horarios libres ese día. Prueba otra fecha.")}</p>
              ) : (
                <div className="grid grid-cols-4 gap-1.5">
                  {reschedSlots.map((t) => (
                    <button
                      key={t}
                      type="button"
                      disabled={saving}
                      onClick={() => doReschedule(t)}
                      className="py-1.5 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:border-primary/60 hover:text-primary transition-colors disabled:opacity-50"
                      data-testid={`reschedule-slot-${t}`}
                    >
                      {formatTime12(t)}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground/70 mt-2 flex items-center gap-1">
                <Clock className="w-3 h-3" />{' '}{tr("Se respetan tu horario, descansos, bloqueos y otras citas.")}</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Row = ({ label, value, gold = false, testId }) => (
  <div className="flex justify-between items-center">
    <span className="text-muted-foreground text-xs">{label}</span>
    <span className={`font-medium text-sm ${gold ? 'font-display font-bold text-primary' : 'text-foreground'}`} data-testid={testId}>{value}</span>
  </div>
);

export default PublicAgendaTimeline;
